import { ApplicationFlowStatePaths } from "src/apps/AppFlow/screens/root";
import { Page } from "src/components/FlowLayout";
import { Memoizer, useMemoizer } from "src/hooks";
import { PageState } from "src/hooks/usePageState";
import { Extractor, ExtractorPath } from "src/hooks/useReducerPageState";
import { find, map } from "src/utilities/object";

import { existsAndNotEmpty, getDeep } from "@fraction/shared";

export interface ItemControl<T extends object, EditContext = any> {
  items: T[];
  // index of item currently being edited
  __editing?: number;
  // index of item currently being deleted
  __target?: number;
  __existing?: T;
  __editContext?: EditContext;
  __lastCommand?: "add" | "edit" | "delete";
}

type Renderer<
  State extends object,
  PageTypes extends string,
  Properties extends string,
  MiddlewareProperties = Record<string, any>
> = Record<
  PageTypes,
  {
    // todo - the MiddlewareProperties will get applied to all renderers, even if
    // they are only put in one of the renderers.
    middleware?: (basePropertyPath: ExtractorPath<State>) => MiddlewareProperties;
    PageRenderer: (
      states: MiddlewareProperties &
        Record<Properties, PageState<any>> & {
          onComplete: () => void;
        },
      path: (string | number)[]
    ) => Page[];
    properties: Properties[];
    basePropertyPath?: (string | number)[];
  }
>;

interface SubpageArgs<
  State extends object = object,
  PageTypes extends string = string,
  Properties extends string = string,
  MiddlewareProperties = Record<string, any>
> {
  state: State;
  basePropertyPath: (string | number)[];
  extractor: Extractor<any>;
  renderers: Renderer<State, PageTypes, Properties, MiddlewareProperties>;
}

export interface Subpages {
  subpages?: Page[];
  onCloseSubpages: () => void;
  onCompleteSubpages: () => void;
  basePropertyPath: (string | number)[];
}

export function useSubpages<
  State extends object = object,
  PageTypes extends string = string,
  Properties extends string = string,
  MiddlewareProperties = Record<string, any>
>({
  state,
  basePropertyPath = [],
  extractor,
  renderers,
}: SubpageArgs<State, PageTypes, Properties, MiddlewareProperties>): Subpages | Partial<Subpages> {
  const memoizer = useMemoizer();

  return (
    findSubpagesBeingEdited({
      state,
      basePropertyPath,
      extractor,
      renderers,
      memoizer,
    }) || {
      onCloseSubpages: defaultOnCloseSubpages,
      onCompleteSubpages: defaultOnCompleteSubpages,
      basePropertyPath: [],
    }
  );
}

const defaultOnCloseSubpages = () => {};
const defaultOnCompleteSubpages = () => {};

export default function useSubpagesInArray<
  State extends object = object,
  PageTypes extends string = string,
  Properties extends string = string,
  MiddlewareProperties = Record<string, any>
>({
  state,
  basePropertyPath,
  extractor,
  renderers,
}: SubpageArgs<State, PageTypes, Properties, MiddlewareProperties>): Subpages {
  const data = existsAndNotEmpty(basePropertyPath) ? getDeep(state, basePropertyPath) : state;
  const memoizer = useMemoizer();

  const found = find(
    map(data, (_: any, i: number) =>
      findSubpagesBeingEdited({
        state,
        basePropertyPath: [...basePropertyPath, i],
        extractor,
        renderers,
        memoizer,
      })
    )
  );

  return (
    found || {
      onCloseSubpages: defaultOnCloseSubpages,
      onCompleteSubpages: defaultOnCompleteSubpages,
      basePropertyPath: [],
    }
  );
}

function findSubpagesBeingEdited<
  State extends object = object,
  PageTypes extends string = string,
  Properties extends string = string,
  MiddlewareProperties = Record<string, any>
>(
  args: { memoizer: Memoizer<any> } & SubpageArgs<State, PageTypes, Properties, MiddlewareProperties>
): Subpages | undefined {
  const { state, basePropertyPath = [], extractor, renderers, memoizer } = args;
  const data = existsAndNotEmpty(basePropertyPath) ? getDeep(state, basePropertyPath) : state;

  // For each renderer, go through and see if
  for (const key of Object.keys(renderers) as PageTypes[]) {
    const editIndex = data?.[key]?.__editing;
    // Check if an item in a given set of items is being edited
    if (editIndex === undefined) {
      continue;
    }

    const name = [
      ...basePropertyPath,
      ...(renderers[key].basePropertyPath || []),
      key,
    ] as ApplicationFlowStatePaths;
    const path = [...name, "items", editIndex];

    // If an item in a given set of items IS being edited, extract
    // the PageState for that item
    const states: Record<Properties, PageState<any>> = renderers[key].properties.reduce((s, property) => {
      s[property] = extractor([...path, property] as ApplicationFlowStatePaths);
      return s;
    }, {} as Record<Properties, PageState<any>>);

    const { onChange, value } = extractor(name);

    const handleComplete = memoizer([path, "handleComplete"], [onChange, value?.items], (): any =>
      onChange({
        items: value?.items,
        __existing: undefined,
        __editing: undefined,
        __editContext: undefined,
      })
    );

    const handleClose = memoizer([path, "handleClose"], [value?.items, value?.__existing, editIndex], () => {
      const newItems = [...(value?.items || [])];
      if (value?.__existing) {
        newItems[editIndex] = value?.__existing;
      } else {
        newItems.splice(editIndex, 1);
      }
      onChange({
        items: newItems,
        __existing: undefined,
        __editing: undefined,
        __editContext: undefined,
      });
    });

    return {
      subpages: renderers[key].PageRenderer(
        // @ts-ignore
        {
          ...states,
          // @ts-ignore
          ...(renderers[key]?.middleware?.(path) || {}),
          onComplete: handleComplete,
        },
        path
      ),
      onCloseSubpages: handleClose,
      onCompleteSubpages: handleComplete,
      basePropertyPath: path,
    };
  }
}
